import React, { useRef, useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Avatar,
  Alert,
  LinearProgress,
  IconButton,
  Typography,
} from '@mui/material'
import { CloudUpload as UploadIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material'
import { SvgIconComponent } from '@mui/icons-material'
import { useImageUpload } from './OvImageUpload.context'
import { observer } from 'mobx-react-lite'

// Types
interface GenericImageUploadProps {
  // Required props
  uploadUrl: string
  currentImageUrl?: string | null

  // Optional props with defaults
  deleteUrl?: string
  title?: string
  fieldName?: string
  icon?: SvgIconComponent | null

  // Validation options
  maxSize?: number
  allowedTypes?: string[]
  maxWidth?: number
  maxHeight?: number

  // UI customization
  avatarSize?: number
  showCard?: boolean
  uploadButtonText?: string
  changeButtonText?: string
  deleteButtonText?: string

  // Callbacks
  onUploadSuccess?: (data: any) => void
  onUploadError?: (error: string) => void
  onDelete?: () => Promise<void>

  // Additional options
  additionalData?: Record<string, any>
  headers?: Record<string, string>
  axiosConfig?: Record<string, any>
}

export const GenericImageUpload: React.FC<GenericImageUploadProps> = observer(
  ({
    // Required props
    uploadUrl,
    deleteUrl,
    currentImageUrl,

    // Optional props with defaults
    title = 'Image Upload',
    fieldName = 'image',
    icon: IconComponent = ImageIcon,

    // Validation options
    maxSize = 2 * 1024 * 1024, // 2MB
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
    maxWidth = 1000,
    maxHeight = 1000,

    // UI customization
    avatarSize = 120,
    showCard = true,
    uploadButtonText = 'Upload Image',
    changeButtonText = 'Change Image',
    deleteButtonText,

    // Callbacks
    onUploadSuccess,
    onUploadError,
    onDelete,

    // Additional options
    additionalData = {},
    headers = {},
    axiosConfig = {},
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [localSuccess, setLocalSuccess] = useState<boolean>(false)

    const { uploading, progress, error, success, uploadImage, deleteImage, clearError, clearSuccess } = useImageUpload()

    // Clear success message after 3 seconds
    useEffect(() => {
      if (success || localSuccess) {
        const timer = setTimeout(() => {
          clearSuccess()
          setLocalSuccess(false)
        }, 3000)
        return () => clearTimeout(timer)
      }
    }, [success, localSuccess, clearSuccess])

    const handleFileSelect = (): void => {
      fileInputRef.current?.click()
    }

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const result = await uploadImage(file, uploadUrl, fieldName, {
        maxSize,
        allowedTypes,
        maxWidth,
        maxHeight,
        additionalData,
        headers,
        axiosConfig,
      })

      if (result.success) {
        setLocalSuccess(true)
        onUploadSuccess?.(result.data)
      } else {
        onUploadError?.(result.error as string)
      }

      // Clear the input
      if (e.target?.value) e.target.value = ''
    }

    const handleDeleteImage = async () => {
      if (onDelete) {
        await onDelete()
      }
    }

    const hasImage = Boolean(currentImageUrl)
    const showSuccess = success || localSuccess

    const getAllowedTypesText = (): string => {
      return allowedTypes.map(type => type.replace('image/', '').toUpperCase()).join(', ')
    }

    const getMaxSizeText = (): string => {
      return `${(maxSize / (1024 * 1024)).toFixed(1)}MB`
    }

    const UploadContent = (): JSX.Element => (
      <>
        {showSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}>
            Image updated successfully!
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={clearError}>
            {error}
          </Alert>
        )}

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}>
          {/* Image Preview */}
          <Avatar
            variant="square"
            src={hasImage ? (currentImageUrl as string) : undefined}
            sx={{
              width: avatarSize,
              height: avatarSize,
              bgcolor: 'grey.200',
              border: '2px dashed',
              borderColor: 'grey.300',
            }}>
            {!hasImage && IconComponent && <IconComponent sx={{ fontSize: avatarSize * 0.4, color: 'grey.500' }} />}
          </Avatar>

          {/* Upload Progress */}
          {uploading && (
            <Box sx={{ width: '100%', maxWidth: 300 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom>
                Uploading... {progress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Box
            display="flex"
            gap={1}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleFileSelect}
              disabled={uploading}>
              {hasImage ? changeButtonText : uploadButtonText}
            </Button>

            {hasImage && deleteUrl && (
              deleteButtonText ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteImage}
                  disabled={uploading}>
                  {deleteButtonText}
                </Button>
              ) : (
                <IconButton
                  color="error"
                  onClick={handleDeleteImage}
                  disabled={uploading}>
                  <DeleteIcon />
                </IconButton>
              )
            )}
          </Box>

          {/* File Requirements */}
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center">
            Supported formats: {getAllowedTypesText()}
            <br />
            Maximum size: {getMaxSizeText()} | Maximum dimensions: {maxWidth}x{maxHeight}px
          </Typography>
        </Box>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedTypes.join(',')}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </>
    )

    if (showCard) {
      return (
        <Card>
          <CardHeader
            title={title}
            avatar={IconComponent && <IconComponent />}
          />
          <CardContent>
            <UploadContent />
          </CardContent>
        </Card>
      )
    }

    return <UploadContent />
  }
)

export default GenericImageUpload
